import React, { useEffect, useState } from 'react';
import { fetchTemplates, createTemplate, uploadTemplateHeaderSample } from '../services/api';
import DashboardContainer from '../components/layout/DashboardContainer';
import TemplatePreview from '../components/templates/TemplatePreview';

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [form, setForm] = useState({ name: '', category: 'UTILITY', language: 'en_US', body_text: '' });
  // Template preview state: media, footer, buttons
  const [mediaType, setMediaType] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [headerType, setHeaderType] = useState(''); // 'TEXT' or 'MEDIA'
  const [headerText, setHeaderText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState([]);
  const [activeTab, setActiveTab] = useState('All');

  // Per-user cache key to prevent cross-client data leaks
  const getCacheKey = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return `tpl_cache_${payload.sub || payload.client_id || 'unknown'}`;
    } catch {
      return null;
    }
  };

  // Instant render from localStorage cache, then background refresh
  useEffect(() => {
    let cancelled = false;
    let hadLocal = false;
    const cacheKey = getCacheKey();

    // Only use cache if we have a user-scoped key
    if (cacheKey) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || '[]');
        if (cached && cached.length) {
          hadLocal = true;
          if (!cancelled) {
            setTemplates(cached);
            setLoading(false);
          }
        }
      } catch { }
    }

    // Fetch using server cache first
    fetchTemplates({ cached: true })
      .then(({ data }) => {
        if (cancelled) return;
        setTemplates(data);
        if (!hadLocal) setLoading(false);
        if (cacheKey) {
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { }
        }
        // Background refresh from Meta
        return fetchTemplates({ cached: false });
      })
      .then((res) => {
        if (cancelled || !res) return;
        const { data } = res;
        if (data && data.length) {
          setTemplates(data);
          if (cacheKey) {
            try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { }
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // If 400 = client not configured for templates, clear any stale cache and show empty
        const status = err.response?.status;
        if (status === 400) {
          setTemplates([]);
          if (cacheKey) { try { localStorage.removeItem(cacheKey); } catch { } }
          setError('');
          setLoading(false);
          return;
        }
        if (!hadLocal) {
          setError('Failed to load templates. Ensure account is connected.');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const load = async (forceRefresh = false) => {
    setLoading(true); setError('');
    try {
      const { data } = await fetchTemplates({ cached: !forceRefresh });
      setTemplates(data);
      const cacheKey = getCacheKey();
      if (cacheKey) {
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch { }
      }
    } catch (e) {
      if (e.response?.status === 400) {
        setTemplates([]);
        setError('');
      } else {
        setError('Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <DashboardContainer>
      <div className="templates-page">
        <div className="card padded">
          <div className="skeleton line" style={{ width: '30%', marginBottom: 8 }} />
          <div className="skeleton block" />
        </div>
      </div>
    </DashboardContainer>
  );
  if (error) return (
    <DashboardContainer>
      <div className="templates-page"><div className="auth-error">{error}</div></div>
    </DashboardContainer>
  );

  const filteredTemplates = templates.filter(t => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Approved') return t.status === 'APPROVED';
    if (activeTab === 'Pending') return t.status !== 'APPROVED' && t.status !== 'REJECTED';
    if (activeTab === 'Rejected') return t.status === 'REJECTED';
    return true;
  });

  return (
    <DashboardContainer>
      <div className="templates-page">
        <div className="page-toolbar">
          <div className="page-header">
            <div>
              <h1>Templates</h1>
              <p>Create templates and view their Meta approval status.</p>
            </div>
            <div className="page-header-actions">
              <button onClick={() => load(true)} className="btn">Refresh</button>
            </div>
          </div>
        </div>

        <div className="templates-grid">
          <div className="templates-top-left">
            <div className="card padded tpl-form-card">
              <h3 className="tpl-form-title">Create a new template</h3>
              {submitError && (
                <div className="auth-error">{submitError}</div>
              )}
              <div className="tpl-form-fields">
                <div className="tpl-form-group">
                  <label>Name</label>
                  <input
                    className="input"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="promo_update_2025"
                  />
                </div>

                <div className="tpl-form-group">
                  <label>Category</label>
                  <select
                    className="select"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                  >
                    <option>UTILITY</option>
                    <option>MARKETING</option>
                    <option>AUTHENTICATION</option>
                  </select>
                </div>

                <div className="tpl-form-group">
                  <label>Language</label>
                  <input
                    className="input"
                    value={form.language}
                    onChange={e => setForm({ ...form, language: e.target.value })}
                    placeholder="en_US"
                  />
                </div>

                <div className="tpl-form-group">
                  <label>Body Text</label>
                  <textarea
                    className="textarea"
                    rows={5}
                    value={form.body_text}
                    onChange={e => setForm({ ...form, body_text: e.target.value })}
                    placeholder="Hello {{1}}, your order {{2}} is ready."
                  />
                </div>

                {/* Header Section */}
                <div className="tpl-form-group">
                  <label>Header (Optional)</label>
                  <select className="select" value={headerType} onChange={e => setHeaderType(e.target.value)}>
                    <option value="">None</option>
                    <option value="TEXT">Text Header</option>
                    <option value="IMAGE">Image Header</option>
                    <option value="VIDEO">Video Header</option>
                    <option value="DOCUMENT">Document Header</option>
                  </select>

                  {headerType === 'TEXT' && (
                    <input
                      className="input"
                      type="text"
                      value={headerText}
                      onChange={e => setHeaderText(e.target.value)}
                      placeholder="Header text with {{1}} variables"
                    />
                  )}

                  {(headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && (
                    <>
                      <input
                        className="input"
                        type="file"
                        accept={headerType === 'IMAGE' ? 'image/*' : headerType === 'VIDEO' ? 'video/*' : '*/*'}
                        onChange={e => setMediaFile(e.target.files[0] || null)}
                      />
                      {mediaFile && (
                        <div className="muted-sm">Selected: {mediaFile.name}</div>
                      )}
                    </>
                  )}
                </div>

                {/* Footer Text */}
                <div className="tpl-form-group">
                  <label>Footer Text (Optional)</label>
                  <input
                    className="input"
                    type="text"
                    value={footerText}
                    onChange={e => setFooterText(e.target.value)}
                    placeholder="Optional footer text (no variables allowed)"
                  />
                </div>

                {/* Interactive Buttons */}
                <div className="tpl-form-group">
                  <label>Call-to-Action Buttons (Max 3)</label>
                  {buttons.map((btn, idx) => (
                    <div key={idx} className="tpl-btn-row">
                      <select
                        value={btn.type}
                        onChange={e => {
                          const arr = [...buttons];
                          arr[idx].type = e.target.value;
                          setButtons(arr);
                        }}
                        className="select" style={{ flex: '0 0 120px' }}
                      >
                        <option value="URL">Website URL</option>
                        <option value="QUICK_REPLY">Quick Reply</option>
                        <option value="PHONE_NUMBER">Phone Number</option>
                      </select>
                      <input
                        type="text"
                        value={btn.text || ''}
                        onChange={e => {
                          const arr = [...buttons];
                          arr[idx].text = e.target.value;
                          setButtons(arr);
                        }}
                        placeholder="Button text"
                        className="input" style={{ flex: 1, minWidth: '120px' }}
                      />
                      {btn.type === 'URL' && (
                        <input
                          type="text"
                          value={btn.url || ''}
                          onChange={e => {
                            const arr = [...buttons];
                            arr[idx].url = e.target.value;
                            setButtons(arr);
                          }}
                          placeholder="https://example.com"
                          className="input" style={{ flex: 1, minWidth: '150px' }}
                        />
                      )}
                      {btn.type === 'PHONE_NUMBER' && (
                        <input
                          type="text"
                          value={btn.phone_number || ''}
                          onChange={e => {
                            const arr = [...buttons];
                            arr[idx].phone_number = e.target.value;
                            setButtons(arr);
                          }}
                          placeholder="+1234567890"
                          className="input" style={{ flex: 1, minWidth: '120px' }}
                        />
                      )}
                      {btn.type === 'QUICK_REPLY' && (
                        <input
                          type="text"
                          value={btn.payload || ''}
                          onChange={e => {
                            const arr = [...buttons];
                            arr[idx].payload = e.target.value;
                            setButtons(arr);
                          }}
                          placeholder="reply_payload_id"
                          className="input" style={{ flex: 1, minWidth: '120px' }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setButtons(buttons.filter((_, i) => i !== idx))}
                        className="btn"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {buttons.length < 3 && (
                    <button
                      type="button"
                      onClick={() => setButtons([...buttons, { type: 'URL', text: '', url: '', payload: '' }])}
                      className="btn"
                    >
                      Add Button
                    </button>
                  )}
                </div>

                <div className="tpl-form-actions">
                  <button
                    onClick={async () => {
                      setCreating(true);
                      setSubmitError('');
                      try {
                        const components = [];
                        if (headerType === 'TEXT' && headerText) {
                          components.push({
                            type: "HEADER", format: "TEXT", text: headerText,
                            example: headerText.includes('{{') ? { header_text: [headerText.replace(/\{\{(\d+)\}\}/g, 'Sample $1')] } : undefined
                          });
                        } else if ((headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && mediaFile) {
                          const { data: sample } = await uploadTemplateHeaderSample(mediaFile);
                          const handle = sample?.handle;
                          if (!handle) {
                            throw new Error('Upload did not return a Meta handle. Try again or create this template in Meta Business Suite.');
                          }
                          components.push({
                            type: "HEADER",
                            format: headerType,
                            example: { header_handle: [handle] },
                          });
                        }
                        components.push({
                          type: "BODY", text: form.body_text,
                          example: form.body_text.includes('{{') ? {
                            body_text: [[...Array.from(form.body_text.matchAll(/\{\{(\d+)\}\}/g), m => `Sample ${m[1]}`)]]
                          } : undefined
                        });
                        if (footerText) components.push({ type: "FOOTER", text: footerText });
                        if (buttons.length > 0) {
                          components.push({
                            type: "BUTTONS",
                            buttons: buttons.map(btn => {
                              if (btn.type === 'URL') return { type: "URL", text: btn.text, url: btn.url };
                              if (btn.type === 'PHONE_NUMBER') return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phone_number };
                              return { type: "QUICK_REPLY", text: btn.text, payload: btn.payload };
                            })
                          });
                        }
                        await createTemplate({ name: form.name, category: form.category, language: form.language, components });
                        const { data } = await fetchTemplates();
                        setTemplates(data);
                        setForm({ name: '', category: 'UTILITY', language: 'en_US', body_text: '' });
                        setHeaderType(''); setHeaderText(''); setMediaFile(null); setFooterText(''); setButtons([]); setSubmitError('');
                      } catch (e) {
                        const detail = e.response?.data?.detail;
                        let message = 'Failed to create template';
                        if (detail) {
                          message = typeof detail === 'string' ? detail : (detail.message || detail.error || detail.meta?.error?.error_user_msg || detail.meta?.error?.message || message);
                        } else if (e.message) message = e.message;
                        setSubmitError(message);
                      } finally { setCreating(false); }
                    }}
                    disabled={
                      creating ||
                      !form.name ||
                      !form.body_text ||
                      ((headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && !mediaFile)
                    }
                    className="btn primary"
                  >
                    {creating ? 'Submitting…' : 'Submit to Meta'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="templates-top-right">
            <div className="card padded card-full-height tpl-preview-sticky">
              <div className="tpl-preview-label">Live preview</div>
              <TemplatePreview
                header={headerType === 'TEXT' ? headerText : null}
                body={form.body_text}
                footer={footerText}
                buttons={buttons}
                media={(headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && mediaFile ? {
                  type: headerType.toLowerCase(),
                  link: URL.createObjectURL(mediaFile)
                } : null}
              />
            </div>
          </div>
        </div>

        <div className="card padded tpl-list-card">
          <div className="tpl-list-header">
            <div className="tpl-list-title">Your templates</div>
            <div className="tabs-inline">
              {['All', 'Approved', 'Pending', 'Rejected'].map(tab => (
                <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
              ))}
            </div>
          </div>

          <div className="templates-list" key={activeTab}>
            {(() => {
              if (templates.length === 0) {
                return <div className="muted-sm">No templates yet.</div>;
              }

              if (filteredTemplates.length === 0) {
                const tabMessages = {
                  'Approved': 'No approved templates.',
                  'Pending': 'No pending templates.',
                  'Rejected': 'No rejected templates.',
                  'All': 'No templates found.'
                };
                return <div className="tpl-empty-state"><div className="muted-sm">{tabMessages[activeTab] || 'No templates found.'}</div></div>;
              }

              return filteredTemplates.map(t => (
                <div key={t.name} className={`template-card ${t.status?.toLowerCase()}`}>
                  <div className="template-card-top">
                    <div>
                      <div className="template-card-name">{t.name}</div>
                      <div className="muted-sm">{t.language} · {t.category}</div>
                    </div>
                    <div>
                      <span className={`badge ${t.status === 'APPROVED' ? 'success' : (t.status === 'REJECTED' ? 'danger' : 'warn')}`}>{t.status}</span>
                    </div>
                  </div>
                  <div className="template-card-body">
                    {(t.components || []).filter(c => (c.type || '').toUpperCase() === 'HEADER').map((c, i) => {
                      const fmt = (c.format || '').toUpperCase();
                      if (['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO'].includes(fmt)) {
                        const icon = fmt === 'IMAGE' ? '🖼' : fmt === 'VIDEO' ? '🎬' : fmt === 'AUDIO' ? '🎵' : '📄';
                        let autoMediaUrl = null;
                        if (c.example) {
                          if (Array.isArray(c.example.header_handle) && c.example.header_handle[0]) {
                            autoMediaUrl = c.example.header_handle[0];
                          } else if (Array.isArray(c.example.header_url) && c.example.header_url[0]) {
                            autoMediaUrl = c.example.header_url[0];
                          } else if (typeof c.example.header_handle === 'string') {
                            autoMediaUrl = c.example.header_handle;
                          }
                        }
                        
                        if (autoMediaUrl && autoMediaUrl.startsWith('http')) {
                          return (
                            <div key={'h_' + i} className="tpl-header" style={{ marginBottom: '8px' }}>
                              {fmt === 'IMAGE' && <img src={autoMediaUrl} alt="header format" style={{ width: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: 8, backgroundColor: '#f0f2f5' }} />}
                              {fmt === 'VIDEO' && <video src={autoMediaUrl} controls style={{ width: '100%', maxHeight: '160px', borderRadius: 8, backgroundColor: '#000' }} />}
                              {(fmt === 'DOCUMENT' || fmt === 'AUDIO') && (
                                <div style={{ borderRadius: 8, background: 'linear-gradient(180deg, #e8ecef, #dfe5e8)', padding: '10px 12px', fontSize: 12, color: '#3b4a54', fontWeight: 600 }}>
                                  {icon} {fmt.charAt(0) + fmt.slice(1).toLowerCase()} header
                                  <div style={{ marginTop: 4 }}><a href={autoMediaUrl} target="_blank" rel="noreferrer" style={{color: '#00a884'}}>View attached media file</a></div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={'h_' + i} className="tpl-header" style={{ marginBottom: '8px' }}>
                            <div
                              style={{
                                borderRadius: 8,
                                background: 'linear-gradient(180deg, #e8ecef, #dfe5e8)',
                                padding: '10px 12px',
                                fontSize: 12,
                                color: '#3b4a54',
                                fontWeight: 600,
                              }}
                            >
                              {icon} {fmt.charAt(0) + fmt.slice(1).toLowerCase()} header
                              <div style={{ fontWeight: 400, fontSize: 11, color: '#667781', marginTop: 4, lineHeight: 1.35 }}>
                                Meta did not provide a preview URL. For campaigns, use a public https link to the media you send.
                              </div>
                            </div>
                          </div>
                        );
                      }
                      return (
                        <div key={'h_' + i} className="tpl-header" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                          {c.text || '(no header text)'}
                        </div>
                      );
                    })}
                    {(t.components || []).filter(c => c.type === 'BODY').map((c, i) => (
                      <pre key={i} className="tpl-body">{c.text || '(no body text)'}</pre>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </DashboardContainer>
  );
};

export default TemplatesPage;
